import { useState, type FormEvent } from 'react';
import { updatePersonSchema } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { Champ } from './Champ';
import { Alerte } from './CadreAuth';
import {
  ChampsDateNaissance,
  composerDate,
  decomposerDate,
  type DateEnTroisChamps,
} from './ChampsDateNaissance';

/**
 * La fiche d'une personne du foyer : lecture, correction, retrait.
 *
 * Regle de la memoire du foyer : ce que Colette sait doit etre corrigible — une fiche
 * qui se trompe et qu'on ne peut pas corriger est pire que pas de fiche. D'ou cette
 * carte : le prenom et la date de naissance se modifient, la personne se retire.
 *
 * Le retrait demande DEUX gestes (« Retirer du foyer » puis « Confirmer le retrait ») :
 * c'est une action destructrice, un pouce qui glisse ne doit pas suffire. Un membre qui
 * a un compte n'a PAS de bouton de retrait : l'API le refuserait (409), et on n'affiche
 * jamais une commande qui ne commande rien.
 */
export function FichePersonne({
  type,
  id,
  titre,
  detail,
  birthDate,
  modifiable,
  retirable,
  onFait,
  signaler,
}: {
  type: 'personne' | 'membre';
  id: string;
  titre: string;
  detail: string;
  birthDate: string | null;
  modifiable: boolean;
  retirable: boolean;
  onFait: (message: string) => void;
  signaler: (message: string) => void;
}) {
  const [edition, setEdition] = useState(false);
  const [nom, setNom] = useState(titre);
  const [naissance, setNaissance] = useState<DateEnTroisChamps>(decomposerDate(birthDate));
  const [retraitArme, setRetraitArme] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const ouvrir = () => {
    // On repart TOUJOURS de la fiche actuelle : une edition abandonnee hier ne doit pas
    // reapparaitre a moitie remplie aujourd'hui.
    setNom(titre);
    setNaissance(decomposerDate(birthDate));
    setRetraitArme(false);
    setErreur(null);
    setEnvoi(false);
    setEdition(true);
  };

  const enregistrer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (envoi) return;
    setErreur(null);

    const jourNaissance = composerDate(naissance);
    if (jourNaissance === null) {
      setErreur('Complète la date : le jour, le mois et l’année (4 chiffres) — ou laisse les trois vides');
      return;
    }

    // Le MEME schema que l'API. Les trois champs vides signifient ici « pas de date » :
    // on envoie null pour effacer, jamais undefined (la fiche dit toujours son etat).
    const verifie = updatePersonSchema.safeParse({
      displayName: nom,
      birthDate: jourNaissance ?? null,
    });
    if (!verifie.success) {
      setErreur(verifie.error.issues[0]?.message ?? 'Vérifie les informations');
      return;
    }

    setEnvoi(true);
    try {
      const fiche = verifie.data as { displayName?: string; birthDate?: string | null };
      if (type === 'membre') await api.modifierMembre(id, fiche);
      else await api.modifierPersonne(id, fiche);
      setEdition(false);
      onFait(`Fiche de ${fiche.displayName ?? titre} mise à jour`);
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  const retirer = async () => {
    if (envoi) return;
    setEnvoi(true);
    try {
      if (type === 'membre') await api.retirerMembre(id);
      else await api.retirerPersonne(id);
      setEdition(false);
      onFait(`${titre} ne fait plus partie du foyer`);
    } catch (cause) {
      signaler(messageDErreur(cause));
      setEnvoi(false);
      setRetraitArme(false);
    }
  };

  if (!edition) {
    return (
      <div
        style={{
          background: C.carte,
          border: `1px solid ${C.bordure}`,
          borderRadius: 14,
          padding: '13px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{titre}</div>
          <div style={{ fontSize: 12, color: C.secondaire }}>{detail}</div>
        </div>
        {modifiable ? (
          <button
            type="button"
            aria-label={`Modifier : ${titre}`}
            onClick={ouvrir}
            style={{
              border: `1px solid ${C.violetBordure}`,
              background: C.carte,
              color: C.violetTexte,
              borderRadius: 10,
              padding: '7px 12px',
              fontSize: 13,
              fontWeight: 800,
              fontFamily: 'inherit',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Modifier
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form
      onSubmit={enregistrer}
      style={{
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        borderRadius: 14,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <Champ libelle="Son prénom" valeur={nom} onChange={setNom} autoFocus />
      <ChampsDateNaissance valeur={naissance} onChange={setNaissance} />

      {erreur !== null ? <Alerte message={erreur} /> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => setEdition(false)}
          style={{
            flex: 1,
            height: 46,
            borderRadius: 12,
            border: `1px solid ${C.bordure}`,
            background: C.carte,
            color: C.secondaire,
            fontSize: 15,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={envoi}
          style={{
            flex: 2,
            height: 46,
            borderRadius: 12,
            border: 'none',
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 15,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
          }}
        >
          {envoi ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {retirable ? (
        <button
          type="button"
          disabled={envoi}
          onClick={() => {
            if (retraitArme) void retirer();
            else setRetraitArme(true);
          }}
          style={{
            height: 44,
            borderRadius: 12,
            border: `1px solid ${retraitArme ? C.corailTexte : C.bordure}`,
            background: retraitArme ? C.corailFond : C.carte,
            color: C.corailTexte,
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
          }}
        >
          {retraitArme ? 'Confirmer le retrait' : 'Retirer du foyer'}
        </button>
      ) : type === 'membre' ? (
        <div style={{ fontSize: 12, color: C.tertiaire, lineHeight: 1.45 }}>
          Cette personne a un compte : elle ne peut pas être retirée d'ici.
        </div>
      ) : null}
    </form>
  );
}
